import { Component, h, Prop, Host, Watch, State } from "@stencil/core";
import { Mission, MissionsResponse } from "./missions-response";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { loadI18n } from "../../utils/initI18nDate";
import { groupBy } from "../../utils/group-by";
import { ButtonSize } from "@trumpf/ux.ui/dist/types/components";

dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);

interface MissionWithWeek extends Mission {
    week: string;
}

export interface MissionsTranslations {
    title: string;
    weekNumber: string;
    missionNumber: string;
    details: string;
    noMissionsText: string;
    noDataText: string;
    linkTitle: string;
    assignmentTypes: {
        [key: string]: string;
    };
    statuses: {
        [key: string]: string;
    };
}

@Component({
    tag: "ux-w-missions",
    styleUrl: "missions.scss",
    shadow: false
})
export class Missions {
    /** locale for calculating the week-number (if not supplied it will be determined from lang attribute on the HTML element) */
    @Prop() locale: string = undefined;
    /** This holds the response from the API with all Information required to show the widget */
    @Prop() data: MissionsResponse = null;
    /** object with translated strings for localization  */
    @Prop() translations: MissionsTranslations = null;
    /** data is loading */
    @Prop() dataLoading: boolean = false;
    /** translations are loading */
    @Prop() translationsLoading: boolean = false;

    @State() finalLocale: string = "en";

    componentWillLoad() {
        this.initLocale();
    }

    @Watch("locale")
    async initLocale() {
        const locale =
            this.locale || document.documentElement.getAttribute("lang");
        loadI18n(locale).then((finalLocale) => {
            this.finalLocale = finalLocale;
        });
    }

    /** translate */
    private t(key: string, fallback?: string): string {
        if (this.translations === null) return fallback || key;
        let node: MissionsTranslations | string = this.translations;
        return key.split(".").reduce<string>((prevValue, currentValue) => {
            if (prevValue !== "" || node === undefined) {
                return key;
            }
            if (typeof node[currentValue] === "string") {
                return node[currentValue];
            }
            node = node[currentValue];
            return prevValue;
        }, "");
    }

    render() {
        const baseClass = "ux-w-missions";
        if (this.dataLoading || this.translationsLoading) {
            return (
                <Host class={baseClass}>
                    <ux-w-wrapper isLoading />
                </Host>
            );
        }
        if (this.data === null) {
            return null;
        }
        const { link, noData } = this.data;
        const hasMissions = this.data?.missions?.length > 0;
        const missions: MissionWithWeek[] = hasMissions
            ? this.data.missions
                  .map((mission) => {
                      const date = dayjs(mission.date).locale(this.finalLocale);
                      return {
                          ...mission,
                          week: `${date.year()}-${date.week()}`
                      };
                  })
                  .sort((a, b) =>
                      dayjs(a.date).isSameOrBefore(dayjs(b.date)) ? -1 : 1
                  )
            : null;
        const groupedMissions: { [key: number]: Mission } =
            missions === null ? {} : groupBy(missions, "week");

        return (
            <Host class={baseClass}>
                <ux-w-wrapper
                    url={link}
                    linkTitle={this.t("linkTitle")}
                    headline={this.t("title")}
                >
                    <div slot="content">
                        {!hasMissions && !noData && (
                            <ux-w-empty
                                class={`${baseClass}__empty`}
                                icon="avatar-wrench-list"
                            >
                                <div
                                    innerHTML={this.t(
                                        "noMissionsText",
                                        "There are no Missions available."
                                    )}
                                    data-testid="no-missions-available"
                                ></div>
                            </ux-w-empty>
                        )}
                        {!hasMissions && noData && (
                            <ux-w-empty icon="crane">
                                <div
                                    innerHTML={this.t(
                                        "noDataText",
                                        "No Data available."
                                    )}
                                    data-testid="no-data-available"
                                ></div>
                            </ux-w-empty>
                        )}
                        {Object.keys(groupedMissions).map((week) => (
                            <div class={`${baseClass}__week-container`}>
                                <div class={`${baseClass}__week-number`}>
                                    <span
                                        class={`${baseClass}__week-number__text`}
                                        data-testid="week-number"
                                    >
                                        {this.t("weekNumber", "Week ")}
                                        {week.split("-")[1]}
                                    </span>
                                </div>
                                {groupedMissions[week].map(
                                    (mission: Mission) => {
                                        const date = dayjs
                                            .utc(mission.date)
                                            .local()
                                            .locale(this.finalLocale);
                                        return (
                                            <div
                                                class={`${baseClass}__item`}
                                                data-testid={`mission-${mission.missionNumber}`}
                                            >
                                                <div
                                                    class={`${baseClass}__assignment-type ux-text-title ux-text-title--bold`}
                                                >
                                                    {this.t(
                                                        `assignmentTypes.${mission.assignmentType}`
                                                    )}
                                                </div>
                                                <div>
                                                    <div
                                                        class={`${baseClass}__date`}
                                                    >
                                                        <span
                                                            class={`${baseClass}__date__day`}
                                                            data-testid="date-day"
                                                        >
                                                            {date.format("DD")}
                                                        </span>
                                                        <span
                                                            class={`${baseClass}__date__month`}
                                                            data-testid="date-month"
                                                        >
                                                            {date.format(
                                                                "MMMM YYYY"
                                                            )}
                                                        </span>
                                                        <div data-testid="date-time">
                                                            {date.format("LT")}
                                                        </div>
                                                    </div>
                                                </div>
                                                <ux-w-status
                                                    class={`${baseClass}__status`}
                                                    type={mission.status}
                                                >
                                                    {this.t(
                                                        `statuses.${mission.status}`
                                                    )}
                                                </ux-w-status>
                                                {"equipmentAlias" in mission ? (
                                                    <div
                                                        class={`${baseClass}__alias ux-text-tiny ux-text-tiny--bold`}
                                                        title={
                                                            mission.equipmentAlias
                                                        }
                                                        data-testid="equipment-alias"
                                                    >
                                                        {mission.equipmentAlias}
                                                    </div>
                                                ) : null}
                                                <div
                                                    class={`${baseClass}__equipment ux-tile-head__subtitle`}
                                                >
                                                    <span data-testid="equipment-name">
                                                        {mission.equipmentName}
                                                    </span>
                                                    <br />
                                                    <span data-testid="equipment-number">
                                                        {
                                                            mission.equipmentNumber
                                                        }
                                                    </span>
                                                </div>
                                                <div
                                                    class={`${baseClass}__label ux-text-tiny ux-text-tiny--bold`}
                                                >
                                                    {this.t(
                                                        "missionNumber",
                                                        "Mission Number"
                                                    )}
                                                </div>
                                                <div
                                                    class="ux-tile-head__subtitle"
                                                    data-testid="mission-number"
                                                >
                                                    {mission.missionNumber}
                                                </div>
                                                <ux-button
                                                    class={`${baseClass}__button`}
                                                    href={mission.link}
                                                    size={"icon" as ButtonSize}
                                                    icon="chevron-right"
                                                    title={this.t(
                                                        "details",
                                                        "Details"
                                                    )}
                                                    data-testid="details"
                                                ></ux-button>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        ))}
                    </div>
                </ux-w-wrapper>
            </Host>
        );
    }
}
